import React, { useState, useRef, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://kiranaos-api-1036279469035.asia-south1.run.app";

const T = {
  saffron: "#E8470A",
  saffronDark: "#C23608",
  saffronLight: "#FDECE4",
  ink: "#1A0A00",
  inkSoft: "#4A2E1A",
  warmWhite: "#FDF6EC",
  warmGray: "#F5EBD9",
  border: "#E8E0D0",
  green: "#1A6B3C",
  greenLight: "#E8F5EE",
  red: "#B3261E",
};

const initialForm = {
  ownerName: "",
  phone: "",
  storeName: "",
  line1: "",
  city: "",
  state: "",
  pincode: "",
  businessType: "Kirana / Grocery / General Store",
};

const STORE_TICKER_ITEMS = [
  ["Adarsh Kirana", "Lucknow"],
  ["Sharma General Store", "Ghaziabad"],
  ["Maa Durga Kirana", "Kanpur"],
  ["Bharat Provision Store", "Jaipur"],
  ["Ravi Daily Needs", "Meerut"],
  ["Annapurna Mart", "Indore"],
  ["Gupta General Store", "Noida"],
  ["Sai Kirana", "Bhopal"],
  ["Fresh Basket Mart", "Gurugram"],
  ["Rajesh Provision Store", "Agra"],
  ["Om Sai General Store", "Faridabad"],
  ["Krishna Daily Needs", "Varanasi"],
  ["City Kirana Mart", "Patna"],
  ["Agarwal Store", "Bareilly"],
  ["Ramesh Kirana", "Prayagraj"],
  ["Mohan General Store", "Aligarh"],
  ["New Bharat Kirana", "Kota"],
  ["Shree Balaji Store", "Udaipur"],
  ["Apna Kirana", "Gwalior"],
  ["Jai Maa Store", "Ranchi"],
  ["Hari Om Provisions", "Jabalpur"],
  ["Kumar Daily Mart", "Dehradun"],
  ["Vishal Kirana", "Moradabad"],
  ["Saraswati Store", "Rohtak"],
  ["Nandini General Store", "Hisar"],
  ["Mahadev Kirana", "Gorakhpur"],
  ["Sanjay Provision Store", "Muzaffarnagar"],
  ["Lakshmi Daily Needs", "Ajmer"],
  ["Ganesh Kirana", "Raipur"],
  ["Deepak General Store", "Bilaspur"],
  ["Asha Kirana", "Jhansi"],
  ["Pooja Daily Mart", "Mathura"],
  ["Vijay Provision Store", "Saharanpur"],
  ["Surya Kirana", "Haldwani"],
  ["Classic General Store", "Ludhiana"],
  ["Rana Daily Needs", "Ambala"],
  ["Satya Kirana", "Rewari"],
  ["Nirmal Store", "Karnal"],
  ["Chaudhary Provisions", "Panipat"],
  ["Kanha Kirana", "Ayodhya"],
  ["Uday General Store", "Bikaner"],
  ["Vikas Daily Mart", "Sikar"],
  ["Shiv Shakti Store", "Haridwar"],
  ["Amit Kirana", "Sonipat"],
  ["Raj General Store", "Panchkula"],
  ["Goyal Daily Needs", "Firozabad"],
  ["Mishra Provision Store", "Satna"],
  ["Yadav Kirana", "Etawah"],
  ["Suman General Store", "Dhanbad"],
  ["Prakash Daily Needs", "Bokaro"],
];

// Strip the 91 country code only when it is actually a prefix on a longer
// number. A plain 10-digit mobile may legitimately start with 91 (e.g.
// 9169119690), so never shorten something that is already 10 digits.
function normalizePhone(value) {
  const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

const S = {
  page: { minHeight: "100vh", background: T.warmGray, color: T.ink, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  nav: { height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 clamp(18px, 6vw, 72px)", borderBottom: `1px solid ${T.border}`, background: T.warmWhite },
  logo: { fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: T.saffron, textDecoration: "none" },
  logoSpan: { color: T.ink, fontWeight: 300 },
  navLink: { color: T.inkSoft, textDecoration: "none", fontSize: 14, fontWeight: 600 },
  shell: { maxWidth: 1040, margin: "0 auto", padding: "42px clamp(16px, 5vw, 40px) 56px" },
  hero: { display: "grid", gridTemplateColumns: "minmax(0, 0.9fr) minmax(340px, 1.1fr)", gap: 34, alignItems: "start" },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 8, color: T.saffronDark, background: T.saffronLight, border: "1px solid rgba(232,119,34,0.28)", borderRadius: 20, padding: "5px 13px", fontSize: 12, fontWeight: 700, marginBottom: 18 },
  dot: { width: 6, height: 6, borderRadius: "50%", background: T.saffron },
  h1: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(36px, 5vw, 58px)", lineHeight: 1.06, letterSpacing: 0, margin: "0 0 18px", fontWeight: 900 },
  lead: { fontSize: 17, lineHeight: 1.75, color: T.inkSoft, margin: "0 0 24px", maxWidth: 560 },
  trustCard: { display: "grid", gap: 12, marginTop: 24, maxWidth: 560 },
  trustItem: { background: "rgba(255,255,255,0.7)", border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" },
  trustTitle: { margin: "0 0 4px", fontSize: 14, fontWeight: 800, color: T.ink },
  trustCopy: { margin: 0, fontSize: 13, lineHeight: 1.55, color: T.inkSoft },
  noteList: { display: "grid", gap: 10, marginTop: 24 },
  note: { display: "grid", gridTemplateColumns: "24px 1fr", gap: 10, color: T.inkSoft, fontSize: 14, lineHeight: 1.6 },
  check: { width: 24, height: 24, borderRadius: "50%", background: T.greenLight, color: T.green, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 },
  formCard: { background: T.warmWhite, border: `1px solid ${T.border}`, borderRadius: 14, padding: "28px clamp(18px, 4vw, 34px)", boxShadow: "0 22px 56px rgba(26,18,8,0.09)" },
  formHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, marginBottom: 20 },
  formTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 800, margin: "0 0 4px" },
  formTitleHi: { color: T.saffronDark, fontSize: 16, fontWeight: 800, margin: "0 0 10px" },
  formSub: { color: T.inkSoft, fontSize: 13, lineHeight: 1.65, margin: 0 },
  formSubHi: { color: T.inkSoft, fontSize: 13, lineHeight: 1.55, margin: "4px 0 0", fontWeight: 700 },
  badge: { background: T.saffronLight, color: T.saffronDark, borderRadius: 20, padding: "5px 11px", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" },
  section: { borderTop: `1px solid ${T.border}`, paddingTop: 22, marginTop: 22 },
  sectionTitle: { color: T.ink, fontSize: 13, fontWeight: 800, margin: "0 0 14px", textTransform: "uppercase", letterSpacing: 0.7 },
  grid2: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 0.75fr", gap: 14 },
  field: { display: "grid", gap: 7, marginBottom: 14 },
  label: { color: T.inkSoft, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 },
  input: { width: "100%", border: `1px solid ${T.border}`, borderRadius: 9, background: "#fff", color: T.ink, fontSize: 15, padding: "12px 14px", outline: "none", fontFamily: "inherit" },
  help: { color: T.inkSoft, fontSize: 12, lineHeight: 1.5, margin: "6px 0 0" },
  upload: { border: `1.5px dashed ${T.border}`, borderRadius: 12, background: "#fff", padding: 16, display: "grid", gap: 10 },
  photoPreview: { width: "100%", maxHeight: 170, objectFit: "cover", borderRadius: 10, border: `1px solid ${T.border}` },
  error: { color: T.red, background: "#fff2f0", border: "1px solid #ffd2cc", borderRadius: 9, padding: "10px 12px", fontSize: 13, lineHeight: 1.5, margin: "12px 0 0" },
  success: { color: T.green, background: T.greenLight, border: "1px solid #bfe8d1", borderRadius: 10, padding: 16, lineHeight: 1.65, marginTop: 14 },
  submitRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 20 },
  submit: { background: T.saffron, color: "#fff", border: "none", borderRadius: 10, padding: "14px 22px", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  footerText: { color: T.inkSoft, fontSize: 12, lineHeight: 1.5, margin: 0 },
  tickerWrap: { background: T.warmWhite, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, overflow: "hidden" },
  tickerInner: { maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: "168px minmax(0, 1fr)", alignItems: "center", gap: 14, padding: "10px clamp(16px, 5vw, 40px)" },
  tickerLabel: { color: T.ink, fontFamily: "'Fraunces', Georgia, serif", fontSize: 16, fontWeight: 800, letterSpacing: 0, whiteSpace: "nowrap" },
  tickerWindow: {
    position: "relative",
    overflow: "hidden",
    minWidth: 0,
    padding: "3px 0",
    WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)",
    maskImage: "linear-gradient(90deg, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)",
  },
  tickerTrack: { display: "flex", alignItems: "center", gap: 8, width: "max-content", animation: "ticker-scroll 155s linear infinite", paddingRight: 8 },
  tickerItem: { display: "inline-flex", alignItems: "center", gap: 7, border: `1px solid rgba(232,224,208,0.85)`, background: "rgba(255,255,255,0.82)", borderRadius: 999, padding: "6px 11px", whiteSpace: "nowrap", fontSize: 12, boxShadow: "0 1px 2px rgba(26,18,8,0.04)" },
  tickerDot: { width: 6, height: 6, borderRadius: "50%", background: T.saffron, flexShrink: 0 },
  tickerName: { fontWeight: 800, color: T.ink },
  tickerCity: { color: T.inkSoft, fontWeight: 500 },
  footer: { background: T.ink, color: "#fff", padding: "30px clamp(18px, 6vw, 72px)", borderTop: "1px solid rgba(255,255,255,0.08)" },
  footerInner: { maxWidth: 1040, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 20 },
  footerLogo: { fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 800, color: T.saffron, margin: "0 0 7px" },
  footerLogoSpan: { color: "rgba(255,255,255,0.42)", fontWeight: 300 },
  footerCopy: { color: "rgba(255,255,255,0.48)", fontSize: 13, lineHeight: 1.6, margin: 0 },
  footerLinks: { display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 18 },
  footerLink: { color: "rgba(255,255,255,0.52)", textDecoration: "none", fontSize: 13, fontWeight: 600 },
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,900&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
  .onboarding-input:focus { border-color: ${T.saffron} !important; }
  .onboarding-submit:hover { background: ${T.saffronDark} !important; }
  @keyframes ticker-scroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @media (max-width: 820px) {
    .onboarding-hero { grid-template-columns: 1fr !important; }
    .onboarding-copy { order: 2; }
    .onboarding-form { order: 1; }
    .onboarding-grid-2, .onboarding-grid-3 { grid-template-columns: 1fr !important; }
    .onboarding-ticker-inner { grid-template-columns: 1fr !important; gap: 8px !important; }
    .onboarding-footer-inner { grid-template-columns: 1fr !important; }
    .onboarding-footer-links { justify-content: flex-start !important; }
  }
`;

export default function OnboardingPage() {
  const [form, setForm] = useState(initialForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef(null);
  // Fires onboarding_start exactly once, on the first field interaction.
  const startedRef = useRef(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const set = (key, value) => {
    setError("");
    if (!startedRef.current && typeof window.gtag === "function") {
      startedRef.current = true;
      // GA4: merchant began filling the onboarding form. No PII sent.
      window.gtag("event", "onboarding_start", {
        page_path: window.location.pathname,
        page_type: "onboarding",
        source_section: "onboarding_form",
      });
    }
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handlePhoneChange = (value) => {
    set("phone", value);
    setOtpSent(false);
    setOtpCode("");
    setOtpVerified(false);
    setOtpError("");
    setResendCooldown(0);
  };

  const sendOtp = async () => {
    const phone = normalizePhone(form.phone);
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setOtpError("Enter a valid 10 digit WhatsApp number before sending OTP.");
      return;
    }
    setOtpSending(true);
    setOtpError("");
    try {
      const res = await fetch(`${API_BASE}/api/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: `+91${phone}` }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to send OTP");
      setOtpSent(true);
      setResendCooldown(30);
      setTimeout(() => otpInputRef.current?.focus(), 50);
    } catch (err) {
      setOtpError(err.message || "Could not send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    const phone = normalizePhone(form.phone);
    if (!otpCode.trim()) {
      setOtpError("Enter the OTP sent to your WhatsApp number.");
      otpInputRef.current?.focus();
      return;
    }
    setOtpVerifying(true);
    setOtpError("");
    try {
      const res = await fetch(`${API_BASE}/api/otp/verify-onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: `+91${phone}`, otp: otpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "OTP verification failed");
      setOtpVerified(true);
    } catch (err) {
      setOtpError(err.message || "Could not verify OTP. Please try again.");
      setTimeout(() => otpInputRef.current?.focus(), 50);
    } finally {
      setOtpVerifying(false);
    }
  };

  const pickPhoto = (file) => {
    setPhotoFile(file || null);
    setError("");
    if (!file) {
      setPhotoPreview("");
      return;
    }
    setPhotoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const phone = normalizePhone(form.phone);
    if (!form.ownerName.trim()) return "Owner name is required.";
    if (!/^[6-9]\d{9}$/.test(phone)) return "Enter a valid 10 digit WhatsApp number.";
    if (!form.storeName.trim()) return "Store name is required.";
    if (!form.line1.trim()) return "Store address is required.";
    if (!form.city.trim()) return "City is required.";
    if (!form.state.trim()) return "State is required.";
    if (!/^\d{6}$/.test(form.pincode.trim())) return "Pincode must be 6 digits.";
    if (!otpVerified) return "Please verify your WhatsApp number with OTP before submitting.";
    if (!photoFile) return "Store photo is required.";
    return "";
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("ownerName", form.ownerName.trim());
      fd.append("phone", normalizePhone(form.phone));
      fd.append("storeName", form.storeName.trim());
      fd.append("line1", form.line1.trim());
      fd.append("city", form.city.trim());
      fd.append("state", form.state.trim());
      fd.append("pincode", form.pincode.trim());
      fd.append("businessType", form.businessType.trim() || "Kirana / Grocery / General Store");
      fd.append("image", photoFile);
      const res = await fetch(`${API_BASE}/api/onboarding/request`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.code === "ONBOARDING_PHONE_NOT_VERIFIED") {
          throw new Error("Your WhatsApp number was not verified. Please verify with OTP and try again.");
        }
        if (data.code === "PHONE_ALREADY_HAS_STORE") {
          throw new Error("This WhatsApp number already has a KiranaOS store. Please login to the dashboard with the same number.");
        }
        if (data.code === "DUPLICATE_ONBOARDING_REQUEST") {
          throw new Error("An onboarding request already exists for this WhatsApp number. Our team will review it soon.");
        }
        throw new Error(data.error || "Submission failed");
      }
      setSuccess({ requestId: data.requestId });
      // GA4: merchant completed onboarding form successfully (lead conversion).
      // Keep legacy `onboarding_submit` for continuity and add the canonical
      // `onboarding_submit_success` name used in the measurement doc. No PII sent.
      if (typeof window.gtag === "function") {
        window.gtag("event", "onboarding_submit", { page: window.location.pathname });
        window.gtag("event", "onboarding_submit_success", {
          page_path: window.location.pathname,
          page_type: "onboarding",
          source_section: "onboarding_form",
        });
      }
      // Meta Pixel: same conversion signal so ad campaigns can optimize for
      // onboarding submits instead of clicks. No personal data is sent.
      if (typeof window.fbq === "function") {
        window.fbq("track", "SubmitApplication");
      }
      setForm(initialForm);
      setPhotoFile(null);
      setPhotoPreview("");
    } catch (err) {
      setError(err.message || "Could not submit onboarding request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main style={S.page}>
      <style>{CSS}</style>
      <nav style={S.nav}>
        <a href="/" style={S.logo}>Kirana<span style={S.logoSpan}>OS</span></a>
        <a style={S.navLink} href="tel:+918860310303">Questions? Call us</a>
      </nav>

      <div style={S.tickerWrap}>
        <div style={S.tickerInner} className="onboarding-ticker-inner">
          <div style={S.tickerLabel}>Stores, now online</div>
          <div style={S.tickerWindow}>
            <div style={S.tickerTrack}>
              {[...STORE_TICKER_ITEMS, ...STORE_TICKER_ITEMS].map(([name, city], index) => (
                <div style={S.tickerItem} key={`${name}-${city}-${index}`}>
                  <span style={S.tickerDot} />
                  <span style={S.tickerName}>{name}</span>
                  <span style={S.tickerCity}>{city}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={S.shell}>
        <section style={S.hero} className="onboarding-hero">
          <div className="onboarding-copy">
            <div style={S.eyebrow}><span style={S.dot} /> Merchant onboarding</div>
            <h1 style={S.h1}>Your store, ready for online orders.</h1>
            <p style={S.lead}>
              Share the details below so we can create your KiranaOS storefront, dashboard, and customer ordering link.
            </p>
            <div style={S.trustCard}>
              <div style={S.trustItem}>
                <p style={S.trustTitle}>Reviewed before launch</p>
                <p style={S.trustCopy}>We check every store before it goes live, so customers see the right name, address, and contact details.</p>
              </div>
              <div style={S.trustItem}>
                <p style={S.trustTitle}>Sent on WhatsApp</p>
                <p style={S.trustCopy}>Once approved, your store link and dashboard access will be sent directly to your WhatsApp number.</p>
              </div>
              <div style={S.trustItem}>
                <p style={S.trustTitle}>Built for your shop</p>
                <p style={S.trustCopy}>Your KiranaOS page carries your store name, address, and business details from day one.</p>
              </div>
            </div>
          </div>

          <div style={S.formCard} className="onboarding-form">
            <div style={S.formHeader}>
              <div>
                <h2 style={S.formTitle}>Let's set up your store</h2>
                <p style={S.formTitleHi}>Apni dukaan online karte hain</p>
                <p style={S.formSub}>Fill this once. We'll create your store link and send it on WhatsApp.</p>
                <p style={S.formSubHi}>Bas basic details chahiye.</p>
              </div>
              <span style={S.badge}>2 min setup</span>
            </div>

            <div>
              <p style={S.sectionTitle}>About you</p>
              <div style={S.grid2} className="onboarding-grid-2">
                <div style={S.field}>
                  <label style={S.label}>Owner name *</label>
                  <input className="onboarding-input" style={S.input} value={form.ownerName} onChange={(e) => set("ownerName", e.target.value)} placeholder="Ramesh Sharma" />
                </div>
                <div style={S.field}>
                  <label style={S.label}>WhatsApp number *</label>
                  <input className="onboarding-input" style={S.input} value={form.phone} onChange={(e) => handlePhoneChange(e.target.value)} placeholder="+91 98765 43210" inputMode="tel" />
                </div>
              </div>

              {/* OTP verification for WhatsApp number */}
              {/^[6-9]\d{9}$/.test(normalizePhone(form.phone)) && (
                <div style={{ marginTop: 12 }}>
                  {otpVerified ? (
                    <p style={{ color: T.green, fontSize: 13, fontWeight: 700, margin: 0 }}>WhatsApp number verified</p>
                  ) : (
                    <div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        {!otpSent ? (
                          <button
                            type="button"
                            style={{ background: T.saffron, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: otpSending ? "default" : "pointer", fontFamily: "inherit", opacity: otpSending ? 0.6 : 1 }}
                            disabled={otpSending}
                            onClick={sendOtp}
                          >
                            {otpSending ? "Sending..." : "Send OTP on WhatsApp"}
                          </button>
                        ) : (
                          <>
                            <input
                              ref={otpInputRef}
                              className="onboarding-input"
                              style={{ ...S.input, width: 120, flex: "0 0 120px" }}
                              value={otpCode}
                              onChange={(e) => { setOtpCode(e.target.value); setOtpError(""); }}
                              placeholder="Enter OTP"
                              inputMode="numeric"
                              maxLength={4}
                            />
                            <button
                              type="button"
                              style={{ background: T.saffron, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: (otpVerifying || !otpCode.trim()) ? "default" : "pointer", fontFamily: "inherit", opacity: (otpVerifying || !otpCode.trim()) ? 0.5 : 1 }}
                              disabled={otpVerifying || !otpCode.trim()}
                              onClick={verifyOtp}
                            >
                              {otpVerifying ? "Verifying..." : "Verify OTP"}
                            </button>
                            <button
                              type="button"
                              style={{ background: "none", border: "none", color: (otpSending || resendCooldown > 0) ? T.inkSoft : T.saffronDark, fontSize: 13, fontWeight: 600, cursor: (otpSending || resendCooldown > 0) ? "default" : "pointer", padding: "0 4px", fontFamily: "inherit" }}
                              disabled={otpSending || resendCooldown > 0}
                              onClick={sendOtp}
                            >
                              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : otpSending ? "Sending..." : "Resend"}
                            </button>
                          </>
                        )}
                      </div>
                      {otpError && (
                        <p style={{ color: T.red, fontSize: 13, fontWeight: 600, margin: "8px 0 0", lineHeight: 1.4 }}>{otpError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={S.section}>
              <p style={S.sectionTitle}>About your shop</p>
              <div style={S.field}>
                <label style={S.label}>Store name *</label>
                <input className="onboarding-input" style={S.input} value={form.storeName} onChange={(e) => set("storeName", e.target.value)} placeholder="Sharma General Store" />
              </div>
              <div style={S.field}>
                <label style={S.label}>Address line *</label>
                <input className="onboarding-input" style={S.input} value={form.line1} onChange={(e) => set("line1", e.target.value)} placeholder="Shop no., market, landmark" />
              </div>
              <div style={S.grid3} className="onboarding-grid-3">
                <div style={S.field}>
                  <label style={S.label}>City *</label>
                  <input className="onboarding-input" style={S.input} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ghaziabad" />
                </div>
                <div style={S.field}>
                  <label style={S.label}>State *</label>
                  <input className="onboarding-input" style={S.input} value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="Uttar Pradesh" />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Pincode *</label>
                  <input className="onboarding-input" style={S.input} value={form.pincode} onChange={(e) => set("pincode", e.target.value)} placeholder="201012" inputMode="numeric" />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Business type *</label>
                <input className="onboarding-input" style={S.input} value={form.businessType} onChange={(e) => set("businessType", e.target.value)} />
              </div>
            </div>

            <div style={S.section}>
              <p style={S.sectionTitle}>Store photo</p>
              <div style={S.upload}>
                {photoPreview && <img src={photoPreview} alt="Store preview" style={S.photoPreview} />}
                <input type="file" accept="image/*" onChange={(e) => pickPhoto(e.target.files?.[0])} />
                <p style={S.help}>Upload a clear photo of the shop front or counter. Fake or AI-generated photos will not be approved.</p>
              </div>
            </div>

            {error && <div style={S.error}>{error}</div>}
            {success && (
              <div style={S.success}>
                Done. We'll check the details and send your KiranaOS store link on WhatsApp.
              </div>
            )}

            {!success && (
              <div style={S.submitRow}>
                <button style={{ ...S.submit, opacity: (!otpVerified || saving) ? 0.5 : 1 }} className="onboarding-submit" type="button" disabled={saving || !otpVerified} onClick={submit}>
                  {saving ? "Sending..." : "Get my store link"}
                </button>
                <p style={S.footerText}>No payment needed now. Free until 31 December 2026, then just ₹35/month. We'll help on WhatsApp if anything is missing.</p>
              </div>
            )}
          </div>
        </section>
      </div>

      <footer style={S.footer}>
        <div style={S.footerInner} className="onboarding-footer-inner">
          <div>
            <p style={S.footerLogo}>Kirana<span style={S.footerLogoSpan}>OS</span></p>
            <p style={S.footerCopy}>India's first AI-native kirana platform. Built by The Works Company.</p>
          </div>
          <div style={S.footerLinks} className="onboarding-footer-links">
            <a style={S.footerLink} href="tel:+918860310303">+91 88603 10303</a>
            <a style={S.footerLink} href="mailto:hello@kiranaos.in">hello@kiranaos.in</a>
            <a style={S.footerLink} href="/privacy">Privacy</a>
            <a style={S.footerLink} href="/terms">Terms</a>
          </div>
        </div>
      </footer>
    </main>
  );
}
